import { Link } from "components/ui/link";
import { Markdown } from "components/ui/Markdown";
import { formatInTimeZone } from "date-fns-tz";
import { ChevronDown, ChevronUp, Users } from "lucide-react";
import React, { useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { cn } from "src/common/utils";

import type {
  AlertEventGroupSeverity,
  AlertGroupContextBanner_alertEventGroup$data,
  AlertGroupContextBanner_alertEventGroup$key,
} from "./__generated__/AlertGroupContextBanner_alertEventGroup.graphql";

// ─── Types ──────────────────────────────────────────────────────────────────

type KnownSeverityType = Exclude<AlertEventGroupSeverity, "%future added value">;

const SEVERITY_CONFIG: Record<KnownSeverityType, { label: string; className: string }> = {
  CRITICAL: { label: "Critical Severity", className: "bg-red-500/15 text-red-400 border-red-500/25" },
  HIGH: { label: "High Severity", className: "bg-red-500/10 text-red-400 border-red-500/20" },
  MEDIUM: { label: "Medium Severity", className: "bg-yellow-500/15 text-yellow-400 border-yellow-500/25" },
  LOW: { label: "Low Severity", className: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
};


// ─── Fragment ───────────────────────────────────────────────────────────────

export const alertGroupContextBannerFragment = graphql`
  fragment AlertGroupContextBanner_alertEventGroup on GraphQLAlertEventGroupSummary {
    id
    label
    status
    severity
    investigationStatus
    eventCount
    investigationCount
    firstSeenAt
    lastSeenAt
    rootCauseSummary
    impactSummary
    investigationDetails {
      id
      name
      status
      createdAt
      alertAgentName
      teamName
    }
  }
`;

// ─── Component ──────────────────────────────────────────────────────────────

interface AlertGroupContextBannerProps {
  alertEventGroup: AlertGroupContextBanner_alertEventGroup$key;
  currentJobRunId: string;
}

export const AlertGroupContextBanner = React.memo<AlertGroupContextBannerProps>(
  function AlertGroupContextBanner({ alertEventGroup: alertEventGroupData, currentJobRunId }) {
    const group = useFragment(alertGroupContextBannerFragment, alertEventGroupData);
    const [isExpanded, setIsExpanded] = useState(false);

    const siblingInvestigations = useMemo(
      () => group.investigationDetails.filter((inv) => inv.id !== currentJobRunId),
      [group.investigationDetails, currentJobRunId],
    );

    const distinctAgentCount = useMemo(() => {
      const agentNames = new Set(
        group.investigationDetails.map((inv) => inv.alertAgentName).filter(Boolean),
      );
      return agentNames.size;
    }, [group.investigationDetails]);

    const severity = group.severity as KnownSeverityType | null;
    const hasExpandedContent = !!(group.rootCauseSummary || group.impactSummary || siblingInvestigations.length > 0);

    const isSingleItemGroup =
      group.eventCount <= 1 && group.investigationCount <= 1 && distinctAgentCount <= 1;

    if (!group.label || isSingleItemGroup) return null;

    return (
      <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/10">
        {/* Collapsed row */}
        <div
          role={hasExpandedContent ? "button" : undefined}
          onClick={hasExpandedContent ? () => setIsExpanded(!isExpanded) : undefined}
          className={cn(
            "flex w-full items-center gap-3 px-4 py-3 text-left",
            hasExpandedContent && "cursor-pointer hover:bg-secondary/50 transition-colors",
          )}
        >
          <span className="text-sm font-medium text-foreground">Group: {group.label}</span>
          {severity && (
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium shrink-0",
                SEVERITY_CONFIG[severity].className,
              )}
            >
              {SEVERITY_CONFIG[severity].label}
            </span>
          )}
          <span className="ml-auto text-xs text-muted-foreground tabular-nums shrink-0">
            {group.eventCount} event{group.eventCount !== 1 ? "s" : ""}
            {" · "}
            {group.investigationCount} investigation{group.investigationCount !== 1 ? "s" : ""}
            {distinctAgentCount > 0 && (
              <>
                {" · "}
                {distinctAgentCount} alert agent{distinctAgentCount !== 1 ? "s" : ""}
              </>
            )}
          </span>
          {hasExpandedContent && (
            isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            )
          )}
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="border-t border-border px-4 py-4 space-y-4">
            {(group.rootCauseSummary || group.impactSummary) && (
              <Markdown>
                {[group.rootCauseSummary, group.impactSummary].filter(Boolean).join(" ").replace(/\n+/g, " ")}
              </Markdown>
            )}

            {/* Sibling investigations */}
            {siblingInvestigations.length > 0 && (
              <div>
                <div className="text-sm text-muted-foreground mb-2">
                  Related Investigations:
                </div>
                <div className="space-y-0 divide-y divide-border/50">
                  {siblingInvestigations.map((inv) => (
                    <SiblingInvestigationRow key={inv.id} investigation={inv} />
                  ))}
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    );
  },
);

// ─── Sibling Investigation Row ──────────────────────────────────────────────

type InvestigationType = AlertGroupContextBanner_alertEventGroup$data["investigationDetails"][number];

interface SiblingInvestigationRowProps {
  investigation: InvestigationType;
}

const SiblingInvestigationRow = React.memo<SiblingInvestigationRowProps>(
  function SiblingInvestigationRow({ investigation: inv }) {
    const agentName = inv.alertAgentName ?? "Investigation";
    const date = formatInTimeZone(new Date(inv.createdAt), "UTC", "MMM d HH:mm") + " UTC";

    return (
      <div className="flex items-center gap-2 py-1.5 text-xs">
        <span className="text-muted-foreground/50">•</span>
        <Link
          to={`/investigations/${inv.id}`}
          className="text-primary hover:text-primary/80 no-underline hover:underline shrink-0"
        >
          {agentName} · {date}
        </Link>
        {inv.teamName && (
          <span className="inline-flex items-center gap-1 rounded border border-indigo-500/15 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-medium text-indigo-400 shrink-0">
            <Users className="h-2.5 w-2.5" />
            {inv.teamName}
          </span>
        )}
      </div>
    );
  },
);
